/* eslint-disable no-param-reassign */
import debounce from 'lodash.debounce';
import {
  TerraDraw,
  TerraDrawPointMode,
  TerraDrawCircleMode,
  TerraDrawRectangleMode,
  TerraDrawPolygonMode,
} from 'terra-draw';
import { TerraDrawGoogleMapsAdapter } from 'terra-draw-google-maps-adapter';
import GOOGLE_MAP_ENUMS from '../../../shared/engage/engage-google-map-enums';

const KM = 1000;
const M_LABEL = 'm';
const KM_LABEL = 'km';

// Map ID required by AdvancedMarkerElement (single source of truth in the shared map enums).
// Re-exported so AnnotateMapPreview can share the same value.
export const GOOGLE_MAP_ID = GOOGLE_MAP_ENUMS().googleMapId;

// Literal replacement for the deprecated google.maps.Marker.MAX_ZINDEX constant (= 1,000,000).
// Keeps cluster markers above individual markers.
const MAX_MARKER_ZINDEX = 1000000;

// Natural size of the location pin asset (pin-purple.png). Set explicitly on the AdvancedMarkerElement
// content <img> so the global `.o-map__content img` rule can't resize/offset it.
const MARKER_PIN_SIZE = 32;

const google = window.google;
const shapeTypes = GOOGLE_MAP_ENUMS().shapeTypes;
const roadmapOverlayStyle = GOOGLE_MAP_ENUMS().roadmapOverlayStyle;
const satelliteOverlayStyle = GOOGLE_MAP_ENUMS().satelliteOverlayStyle;
const markerStyle = GOOGLE_MAP_ENUMS().markerStyle;

// AdvancedMarkerElement.position reads back as a LatLng (methods) or a literal (number props),
// depending on how it was set. Normalize to a plain { lat, lng } literal.
function markerPositionToLiteral(position) {
  if (!position) return { lat: 0, lng: 0 };
  return typeof position.lat === 'function'
    ? { lat: position.lat(), lng: position.lng() }
    : { lat: position.lat, lng: position.lng };
}

const clusterRenderer = {
  render: ({ count, position }, stats, map) => {
    // change color if this cluster has more markers than the mean cluster
    const color = count > Math.max(10, stats.clusters.markers.mean) ? '#DF543F' : '#0C87E1';
    // create svg literal with fill color
    const svg = `<svg fill="${color}" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 240 240" width="50" height="50">
                  <circle cx="120" cy="120" opacity=".8" r="70" />
                  <circle cx="120" cy="120" opacity=".3" r="90" />
                  <circle cx="120" cy="120" opacity=".2" r="110" />
                  <text x="50%" y="50%" style="fill:#fff" text-anchor="middle" font-size="50" dominant-baseline="middle" font-family="roboto,arial,sans-serif">${count}</text>
                  </svg>`;
    const title = `Cluster of ${count} markers`;
    // adjust zIndex to be above other markers
    const zIndex = MAX_MARKER_ZINDEX + count;
    // create cluster SVG element
    const parser = new DOMParser();
    const svgEl = parser.parseFromString(svg, 'image/svg+xml').documentElement;
    svgEl.setAttribute('transform', 'translate(0 25)');
    return new google.maps.marker.AdvancedMarkerElement({
      map,
      position,
      zIndex,
      title,
      content: svgEl,
    });
  },
};

function getCustomMapUrl(customMapLayer) {
  if (!customMapLayer) {
    return '';
  }
  return customMapLayer.getUrl().split('&ver')[0];
}

function drawCustomMapLayer(map, customMapUrl, customMapLayer) {
  let newCustomMapLayer = null;

  const existingCustomMapUrl = getCustomMapUrl(customMapLayer);

  if ((customMapUrl && !customMapLayer) || (customMapUrl && customMapLayer && customMapUrl !== existingCustomMapUrl)) {
    // In case custom map URL is added or replaced create custom map layer and add it to the map
    if (customMapLayer) customMapLayer.setMap(null);

    const synthUrl = `${customMapUrl}&ver=${new Date().getTime()}`;
    newCustomMapLayer = new google.maps.KmlLayer(synthUrl, {
      preserveViewport: true,
      map,
    });
  } else if (customMapUrl === '' && customMapLayer) {
    // Clear custom map layer
    customMapLayer.setMap(null);
  }

  return newCustomMapLayer;
}

// Google removed the `drawing` library (DrawingManager) in Maps JS API v3.65. Terra Draw replaces
// the shape-CREATION step only; once a shape is finished it is handed off to the existing native
// google.maps overlay render/edit code, so the persisted mapData format is unchanged. See
// docs/plans/2509-drawingmanager-terradraw/PLAN.md.

const BRAND_COLOR = '#070047';
const TERRA_DRAW_STYLE = { fillColor: BRAND_COLOR, fillOpacity: 0.1, outlineColor: BRAND_COLOR, outlineWidth: 3 };

// Our shapeTypes (engage enums) <-> Terra Draw mode names. They match except marker <-> point.
const TERRA_MODE_BY_SHAPE = {
  [shapeTypes.CIRCLE]: 'circle',
  [shapeTypes.POLYGON]: 'polygon',
  [shapeTypes.RECTANGLE]: 'rectangle',
  [shapeTypes.MARKER]: 'point',
};
const SHAPE_BY_TERRA_MODE = {
  circle: shapeTypes.CIRCLE,
  polygon: shapeTypes.POLYGON,
  rectangle: shapeTypes.RECTANGLE,
  point: shapeTypes.MARKER,
};

function shapeTypeToTerraMode(shapeType) {
  return TERRA_MODE_BY_SHAPE[shapeType];
}

function terraModeToShapeType(mode) {
  return SHAPE_BY_TERRA_MODE[mode];
}

// Build a Terra Draw instance bound to the Google map. Idle in the built-in 'static' mode until the
// user picks a draw tool (MapComponent calls draw.setMode(...)).
//
// start() is deferred to the map's first 'idle': the Google Maps adapter registers DOM listeners on
// the map's container, which only exists after Maps renders its first frame. Calling start() in the
// same tick the map is constructed throws "Cannot read properties of null (reading 'addEventListener')".
function setUpTerraDraw(map) {
  const draw = new TerraDraw({
    adapter: new TerraDrawGoogleMapsAdapter({ map, lib: google.maps }),
    modes: [
      new TerraDrawPointMode(),
      new TerraDrawCircleMode({ styles: TERRA_DRAW_STYLE }),
      new TerraDrawRectangleMode({ styles: TERRA_DRAW_STYLE }),
      new TerraDrawPolygonMode({ styles: TERRA_DRAW_STYLE }),
    ],
  });
  google.maps.event.addListenerOnce(map, 'idle', () => {
    draw.start();
    draw.setMode('static');
  });
  return draw;
}

// Subscribe to Terra Draw's 'finish' event (fired when a shape is completed). action === 'draw'
// filters out edit/drag finishes. The finished GeoJSON feature is handed to onDrawFinish.
function attachTerraDrawHandlers(draw, onDrawFinish) {
  draw.on('finish', (id, context) => {
    if (context.action !== 'draw') return;
    const feature = draw.getSnapshotFeature(id);
    if (feature) onDrawFinish(feature);
  });
}

// ---- Pure GeoJSON (Terra Draw) -> populo mapData converters (no google.maps dependency) ----

function isClosedRing(ring) {
  if (ring.length < 2) return false;
  const a = ring[0];
  const b = ring[ring.length - 1];
  return a[0] === b[0] && a[1] === b[1];
}

function uniqueVertices(ring) {
  return isClosedRing(ring) ? ring.slice(0, -1) : ring;
}

function ringToBounds(ring) {
  let minLat = Infinity;
  let maxLat = -Infinity;
  let minLng = Infinity;
  let maxLng = -Infinity;
  ring.forEach(([lng, lat]) => {
    if (lat < minLat) minLat = lat;
    if (lat > maxLat) maxLat = lat;
    if (lng < minLng) minLng = lng;
    if (lng > maxLng) maxLng = lng;
  });
  return { ne: { lat: maxLat, lng: maxLng }, sw: { lat: minLat, lng: minLng } };
}

function ringCentroid(ring) {
  const pts = uniqueVertices(ring);
  let sumLng = 0;
  let sumLat = 0;
  pts.forEach(([lng, lat]) => {
    sumLng += lng;
    sumLat += lat;
  });
  return { lat: sumLat / pts.length, lng: sumLng / pts.length };
}

function ringToPath(ring) {
  return uniqueVertices(ring).map(([lng, lat]) => ({ lat, lng }));
}

// Convert a finished Terra Draw GeoJSON feature into the existing mapData shape that
// drawDefinedAreaOverlay (and the persisted/Client-read format) expects.
function terraFeatureToMapData(feature) {
  const mode = feature.properties.mode;
  const mapData = {
    shape: terraModeToShapeType(mode),
    circle: null,
    rectangle: null,
    polygon: null,
    point: null,
  };

  if (mode === 'point') {
    const [lng, lat] = feature.geometry.coordinates;
    mapData.point = { lat, lng };
    return mapData;
  }

  const ring = feature.geometry.coordinates[0];
  const bounds = ringToBounds(ring);

  if (mode === 'circle') {
    mapData.circle = {
      center: ringCentroid(ring),
      radius: (feature.properties.radiusKilometers || 0) * 1000, // km -> m, matches google.maps.Circle
      bounds,
    };
  } else if (mode === 'rectangle') {
    mapData.rectangle = { bounds };
  } else if (mode === 'polygon') {
    mapData.polygon = { path: ringToPath(ring) };
  }

  return mapData;
}

function setMapBounds(definedArea, newCustomMapLayer, map) {
  if (newCustomMapLayer && !google.maps.event.hasListeners(newCustomMapLayer, 'defaultviewport_changed')) {
    // Wait for custom map to load and then set the final bounds
    google.maps.event.addListener(newCustomMapLayer, 'defaultviewport_changed', (event) => {
      const newLayerBounds = newCustomMapLayer ? newCustomMapLayer.getDefaultViewport() : null;
      const definedAreaBounds = definedArea?.bounds;
      if (newLayerBounds && definedAreaBounds) {
        const newBounds = newLayerBounds.union(definedAreaBounds);
        map.fitBounds(newBounds);
      } else if (newLayerBounds) {
        map.fitBounds(newLayerBounds);
      } else if (definedAreaBounds) {
        map.fitBounds(definedAreaBounds);
      }
    });
  }
}

function setDefinedAreaInteractivity(map, definedArea, customMapUrl) {
  if (!map || !definedArea) return;

  // Marker overlays are AdvancedMarkerElement (no setOptions, and the fill/stroke overlay style is
  // irrelevant to a pin) — nothing to toggle here.
  if (definedArea.type === shapeTypes.MARKER) return;

  const newOverlayStyle =
    map.getMapTypeId() === google.maps.MapTypeId.ROADMAP ? roadmapOverlayStyle : satelliteOverlayStyle;

  if (
    definedArea.type === shapeTypes.CIRCLE ||
    definedArea.type === shapeTypes.RECTANGLE ||
    definedArea.type === shapeTypes.POLYGON
  ) {
    newOverlayStyle.clickable = customMapUrl === '' || !customMapUrl;
  }

  definedArea.overlay.setOptions(newOverlayStyle);
}

function drawDefinedAreaOverlay(map, mapData, hasClickableOverlays) {
  let shape;
  let bounds;
  const currentOverlayStyle =
    mapData.mapTypeId === google.maps.MapTypeId.ROADMAP || !mapData.mapTypeId
      ? roadmapOverlayStyle
      : satelliteOverlayStyle;

  currentOverlayStyle.clickable = hasClickableOverlays;

  switch (mapData.shape) {
    case shapeTypes.CIRCLE: {
      shape = new google.maps.Circle({
        map,
        center: new google.maps.LatLng(mapData.circle.center.lat, mapData.circle.center.lng),
        radius: mapData.circle.radius,
        ...currentOverlayStyle,
      });

      bounds = shape.getBounds();
      break;
    }
    case shapeTypes.POLYGON: {
      const polygonCoords = [];
      mapData.polygon.path.forEach((coordinate) => {
        polygonCoords.push({ lat: coordinate.lat, lng: coordinate.lng });
      });

      shape = new google.maps.Polygon({
        map,
        paths: polygonCoords,
        ...currentOverlayStyle,
      });

      bounds = new google.maps.LatLngBounds();
      polygonCoords.forEach((coord) => {
        bounds.extend(coord);
      });
      break;
    }
    case shapeTypes.RECTANGLE: {
      const north = mapData.rectangle.bounds.ne.lat;
      const south = mapData.rectangle.bounds.sw.lat;
      const east = mapData.rectangle.bounds.ne.lng;
      const west = mapData.rectangle.bounds.sw.lng;

      shape = new google.maps.Rectangle({
        map,
        bounds: { north, south, east, west },
        ...currentOverlayStyle,
      });

      bounds = shape.getBounds();
      break;
    }
    case shapeTypes.MARKER: {
      // AdvancedMarkerElement anchors the bottom-center of its content box at the point. A bare <img>
      // content gets sized to a 56px-tall box on the map (the global `.o-map__content img` rule sets
      // width/height:100%), which seats the anchor 24px below the pin tip — the pin then floats above
      // its true location. Wrapping the icon in a fixed 32x32 box gives Google a definite content box
      // so the pin tip lands exactly on the point. See sdesregistry/IH-Engage#2515.
      const pin = document.createElement('div');
      pin.style.width = `${MARKER_PIN_SIZE}px`;
      pin.style.height = `${MARKER_PIN_SIZE}px`;
      pin.style.lineHeight = '0';
      pin.style.backgroundImage = `url('${markerStyle.icon}')`;
      pin.style.backgroundSize = 'contain';
      pin.style.backgroundRepeat = 'no-repeat';
      shape = new google.maps.marker.AdvancedMarkerElement({
        map,
        position: mapData.point,
        content: pin,
        gmpDraggable: markerStyle.draggable,
        zIndex: markerStyle.zIndex,
      });

      map.setCenter(mapData.point);
      if (mapData.zoom) map.setZoom(parseInt(mapData.zoom, 10));

      break;
    }
    default:
      break;
  }

  return { shape, bounds };
}

function setMapPositionAndZoomLevel(map, areaBounds, zoomLevel, memorizeZoomLevel) {
  if (!areaBounds) return;

  if (memorizeZoomLevel && zoomLevel) {
    map.setCenter(areaBounds.getCenter());
    map.setZoom(parseInt(zoomLevel, 10));
  } else {
    map.fitBounds(areaBounds);
  }
}

function getValueInUnits(value, showInMeters) {
  return showInMeters ? Math.round(value) : Math.round(value / 100) / 10;
}

function getBoundsWidthAndHeight(bounds) {
  const sePoint = new google.maps.LatLng(bounds.getSouthWest().lat(), bounds.getNorthEast().lng());
  const width = google.maps.geometry.spherical.computeDistanceBetween(bounds.getSouthWest(), sePoint);
  const height = google.maps.geometry.spherical.computeDistanceBetween(bounds.getNorthEast(), sePoint);

  return { width, height };
}

function determinePolygonDrawnAreaInfo(bounds, setDrawnAreaWidth, setDrawnAreaHeight, setDrawnAreaUnit) {
  const sePoint = new google.maps.LatLng(bounds.getSouthWest().lat(), bounds.getNorthEast().lng());
  const areaWidth = google.maps.geometry.spherical.computeDistanceBetween(bounds.getSouthWest(), sePoint);
  const areaHeight = google.maps.geometry.spherical.computeDistanceBetween(bounds.getNorthEast(), sePoint);

  const showInMeters = areaWidth < KM && areaHeight < KM;
  setDrawnAreaWidth(getValueInUnits(areaWidth, showInMeters));
  setDrawnAreaHeight(getValueInUnits(areaHeight, showInMeters));
  setDrawnAreaUnit(showInMeters ? M_LABEL : KM_LABEL);
}

function determineCircleDrawnAreaInfo(shape, setDrawnAreaRadius, setDrawnAreaUnit) {
  const radius = shape.getRadius();
  const center = shape.getCenter();

  const showInMeters = radius < KM;
  setDrawnAreaRadius(getValueInUnits(radius, showInMeters));
  setDrawnAreaUnit(showInMeters ? M_LABEL : KM_LABEL);
}

function prepareCircleData(shape) {
  const radius = shape.getRadius();
  const center = shape.getCenter();
  const circle = {};
  const bounds = shape.getBounds();
  circle.bounds = {
    ne: { lat: bounds.getNorthEast().lat(), lng: bounds.getNorthEast().lng() },
    sw: { lat: bounds.getSouthWest().lat(), lng: bounds.getSouthWest().lng() },
  };
  circle.center = { lat: center.lat(), lng: center.lng() };
  circle.radius = radius;

  return circle;
}

function prepareRectangleData(bounds) {
  const rectangle = {};
  rectangle.bounds = {
    ne: { lat: bounds.getNorthEast().lat(), lng: bounds.getNorthEast().lng() },
    sw: { lat: bounds.getSouthWest().lat(), lng: bounds.getSouthWest().lng() },
  };

  return rectangle;
}

function preparePolygonData(shape) {
  const polygon = {};
  const polygonPaths = [];
  shape.getPath().forEach((point) => {
    polygonPaths.push({ lat: point.lat(), lng: point.lng() });
  });

  polygon.path = polygonPaths;

  return polygon;
}

function preparePointData(shape) {
  // shape is an AdvancedMarkerElement (no getPosition()); read its .position via the normalizer.
  return markerPositionToLiteral(shape.position);
}

function setUpMapInstance(
  id,
  mapOptions,
  mapData,
  mapZoom,
  mapType,
  mapStyle,
  showMapTypeControl,
  showZoomControl,
  showFullscreenControl,
  showScaleControl,
  showStreetViewControl,
) {
  const map = new google.maps.Map(document.getElementById(`map-${id}`), {
    center: new google.maps.LatLng(mapOptions.lat, mapOptions.lng),
    zoom: mapData.shape ? mapZoom : mapOptions.zoom,
    mapId: GOOGLE_MAP_ID,
    // Force raster rendering: overrides the Map ID's vector config so AdvancedMarkerElement still
    // works while KmlLayer (custom map layers via drawCustomMapLayer) renders reliably. See #2515.
    renderingType: google.maps.RenderingType.RASTER,
    mapTypeId: mapType || google.maps.MapTypeId.ROADMAP,
    disableDefaultUI: true,
    mapTypeControl: showMapTypeControl,
    mapTypeControlOptions: {
      style: google.maps.MapTypeControlStyle.HORIZONTAL_BAR,
      mapTypeIds: [google.maps.MapTypeId.ROADMAP, google.maps.MapTypeId.SATELLITE],
      position: google.maps.ControlPosition.TOP_RIGHT,
    },
    styles: mapStyle,
    zoomControl: showZoomControl,
    zoomControlOptions: {
      position: google.maps.ControlPosition.RIGHT_BOTTOM,
    },
    fullscreenControl: showFullscreenControl,
    scaleControl: showScaleControl,
    streetViewControl: showStreetViewControl,
  });

  return map;
}

// Zoom applied when a searched place has no viewport (a precise point rather than an area).
const SEARCH_RESULT_ZOOM = 14;

// Pure: given a fetched Place, decide where the camera should go. Geocodes carry a viewport
// (fit to it); a precise point has only a location (center on it). Returns null when the place has
// no usable geometry. No google.maps dependency, so this is unit-testable in jsdom.
function placeCameraTarget(place) {
  if (!place) return null;
  if (place.viewport) return { kind: 'bounds', value: place.viewport };
  if (place.location) return { kind: 'center', value: place.location };
  return null;
}

function setUpSearchControl(id, map) {
  const inputWrapper = document.getElementById(`${id}-map-search-input-wrapper`);
  if (!inputWrapper) return;

  // PlaceAutocompleteElement is the Places API (New) successor to google.maps.places.SearchBox,
  // which became legacy on 2025-03-01 (#2513). The element renders its own input; we host it in our
  // styled wrapper and push that into the map's control layer.
  const placeAutocomplete = new google.maps.places.PlaceAutocompleteElement();
  // Force light theme. The Places UI Kit element otherwise follows the OS/browser `color-scheme` and
  // renders dark in dark mode, clashing with the always-light map UI.
  placeAutocomplete.style.colorScheme = 'light';
  inputWrapper.appendChild(placeAutocomplete);
  map.controls[google.maps.ControlPosition.TOP_LEFT].push(inputWrapper);

  // Bias (not restrict) predictions toward the current viewport so nearby results rank first while
  // still allowing a search anywhere.
  map.addListener('bounds_changed', () => {
    placeAutocomplete.locationBias = map.getBounds();
  });

  // On selection, recenter the map to the chosen place. Unlike the legacy SearchBox (which returned
  // an array of results + dropped markers) this resolves a single place — the right behavior for a
  // "recenter to set up the area" helper.
  placeAutocomplete.addEventListener('gmp-select', async ({ placePrediction }) => {
    const place = placePrediction.toPlace();
    await place.fetchFields({ fields: ['location', 'viewport'] });

    const target = placeCameraTarget(place);
    if (!target) return;
    if (target.kind === 'bounds') {
      map.fitBounds(target.value);
    } else {
      map.setCenter(target.value);
      map.setZoom(SEARCH_RESULT_ZOOM);
    }
  });
}

function attachMapEventHandlers(map, memorizeZoomLevel, setMapZoom, setMapType) {
  // Events dispatched when user changes map type or zoom level
  map.addListener('maptypeid_changed', () => {
    setMapType(map.getMapTypeId());
  });

  map.addListener('zoom_changed', () => {
    if (memorizeZoomLevel) setMapZoom(map.getZoom());
  });
}

function attachDefinedAreaEventHandlers(definedArea, handleOverlayEvents) {
  // Event dispatched when user moves or changes dimensions of defined area on the map
  ['dragend', 'radius_changed', 'mouseup'].forEach((event) => {
    google.maps.event.addListener(definedArea.overlay, event, () =>
      handleOverlayEvents(definedArea.overlay, definedArea.type),
    );
  });

  ['center_changed', 'bounds_changed'].forEach((event) => {
    google.maps.event.addListener(
      definedArea.overlay,
      event,
      debounce(() => {
        handleOverlayEvents(definedArea.overlay, definedArea.type);
      }, 400),
    );
  });
}

async function getMaxZoomLevel(latLng) {
  let maxZoomLevel = 10;
  try {
    const maxZoomService = new google.maps.MaxZoomService();
    const res = await maxZoomService.getMaxZoomAtLatLng(latLng);
    maxZoomLevel = res.zoom;
  } catch (error) {
    // console.log('error ');
  }
  return maxZoomLevel;
}

export default {
  clusterRenderer,
  markerPositionToLiteral,
  getCustomMapUrl,
  drawCustomMapLayer,
  setUpTerraDraw,
  attachTerraDrawHandlers,
  shapeTypeToTerraMode,
  terraModeToShapeType,
  terraFeatureToMapData,
  setMapBounds,
  setDefinedAreaInteractivity,
  drawDefinedAreaOverlay,
  setMapPositionAndZoomLevel,
  getBoundsWidthAndHeight,
  determinePolygonDrawnAreaInfo,
  determineCircleDrawnAreaInfo,
  prepareCircleData,
  prepareRectangleData,
  preparePolygonData,
  preparePointData,
  setUpMapInstance,
  setUpSearchControl,
  placeCameraTarget,
  attachMapEventHandlers,
  attachDefinedAreaEventHandlers,
  getMaxZoomLevel,
};
